import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import api, { updatePago, deletePago, deleteAbonoGrupo, uploadComprobantePago, deleteComprobantePago } from '../lib/api';
import { exportToExcel } from '../lib/exportUtils';

import {
  MAX_COMPROBANTES, ALLOWED_TYPES, MAX_FILE_MB, isPdfUrl,
  formatCurrency, formatDate,
} from './cxc/utils';
import { KPICards } from './cxc/KPICards';
import { FacturasPendientesTab } from './cxc/FacturasPendientesTab';
import { HistorialPagosTab } from './cxc/HistorialPagosTab';
import { ClientesTab } from './cxc/ClientesTab';
import { AbonoClienteTab } from './cxc/AbonoClienteTab';
import { PagoDialog } from './cxc/PagoDialog';
import { PagoEditDialog } from './cxc/PagoEditDialog';
import { EstadoCuentaDialog } from './cxc/EstadoCuentaDialog';
import { VerComprobantesDialog } from './cxc/VerComprobantesDialog';

const INITIAL_FORM = {
  factura_id: '',
  fecha: new Date().toISOString().split('T')[0],
  monto: '',
  metodo_pago: 'Efectivo',
  referencia: '',
  observaciones: '',
};

export default function CuentasPorCobrar() {
  const [resumenCxC, setResumenCxC] = useState({
    total_por_cobrar: 0,
    total_vencido: 0,
    total_por_vencer: 0,
    num_facturas_pendientes: 0,
  });
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [modalPagoOpen, setModalPagoOpen] = useState(false);
  const [modalEstadoCuentaOpen, setModalEstadoCuentaOpen] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [verComprobantesPago, setVerComprobantesPago] = useState(null);

  // Filtros / formularios
  const [filtroCliente, setFiltroCliente] = useState('all');
  const [formPago, setFormPago] = useState(INITIAL_FORM);
  const [comprobantes, setComprobantes] = useState([]);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);

  // Edit / Delete
  const [editingPagoId, setEditingPagoId] = useState(null);
  const [deletingPago, setDeletingPago] = useState(null);
  const [savingPago, setSavingPago] = useState(false);
  const [deletingPagoInProgress, setDeletingPagoInProgress] = useState(false);
  // Nuevo modal por cliente (registrar pago FIFO). Diferente del modal de edición.
  const [modalRegistrarOpen, setModalRegistrarOpen] = useState(false);
  const [registrarClienteId, setRegistrarClienteId] = useState(null);
  // Edición de un abono (grupo de pagos). Reutiliza PagoDialog en modo edit.
  const [editingAbono, setEditingAbono] = useState(null);

  // ============================================================
  // Data
  // ============================================================
  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [resumenRes, facturasRes, clientesRes, pagosRes] = await Promise.all([
        api.get('/cxc/resumen'),
        api.get('/facturas/pendientes'),
        api.get('/clientes'),
        api.get('/pagos?limit=50'),
      ]);
      setResumenCxC(resumenRes.data);
      setFacturasPendientes(facturasRes.data);
      setClientes(clientesRes.data);
      setPagos(pagosRes.data);
    } catch (error) {
      console.error('Error cargando datos CxC:', error);
      toast.error('Error al cargar datos de Cuentas por Cobrar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // ============================================================
  // Pago handlers
  // ============================================================
  const abrirModalPago = (factura = null) => {
    setEditingPagoId(null);
    setComprobantes([]);
    // Si viene una factura seleccionada (desde la tabla), pre-seleccionamos su cliente.
    setRegistrarClienteId(factura?.cliente_id || null);
    setModalRegistrarOpen(true);
  };

  const abrirModalEditarPago = (grupoOrPago) => {
    // El historial ahora envía un GRUPO (con .items, .grupo_id, moneda, etc.).
    // Si tiene grupo_id, editar reutilizando el PagoDialog en modo edición.
    if (grupoOrPago && grupoOrPago.grupo_id) {
      setEditingAbono(grupoOrPago);
      // Precargar comprobantes del grupo al estado compartido
      const existentes = Array.isArray(grupoOrPago.comprobantes) ? grupoOrPago.comprobantes : [];
      setComprobantes(existentes.map((it) => {
        if (typeof it === 'string') {
          const parts = it.split('/');
          return {
            url: it,
            filename: parts[parts.length - 1] || 'comprobante',
            content_type: isPdfUrl(it) ? 'application/pdf' : 'image/*',
          };
        }
        return it;
      }));
      setRegistrarClienteId(grupoOrPago.cliente_id || null);
      setModalRegistrarOpen(true);
      return;
    }
    // Compat: pago suelto sin grupo → flujo antiguo (single-row)
    const pago = grupoOrPago && grupoOrPago.items ? grupoOrPago.items[0] : grupoOrPago;
    setEditingPagoId(pago.id || pago._id);
    setFormPago({
      factura_id: pago.factura_id || '',
      fecha: pago.fecha?.split('T')[0] || new Date().toISOString().split('T')[0],
      monto: String(pago.monto ?? ''),
      metodo_pago: pago.metodo_pago || 'Efectivo',
      referencia: pago.referencia || '',
      observaciones: pago.observaciones || '',
    });
    const existentes = Array.isArray(pago.comprobantes) ? pago.comprobantes : [];
    setComprobantes(existentes.map((it) => {
      if (typeof it === 'string') {
        const parts = it.split('/');
        return {
          url: it,
          filename: parts[parts.length - 1] || 'comprobante',
          content_type: isPdfUrl(it) ? 'application/pdf' : 'image/*',
        };
      }
      return it;
    }));
    setModalPagoOpen(true);
  };

  const handleSelectComprobantes = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const remaining = MAX_COMPROBANTES - comprobantes.length;
    if (remaining <= 0) {
      toast.warning(`Máximo ${MAX_COMPROBANTES} comprobantes por pago`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > toUpload.length) {
      toast.info(`Solo se subirán ${toUpload.length} de ${files.length} archivos (límite: ${MAX_COMPROBANTES})`);
    }
    setUploadingComprobante(true);
    for (const file of toUpload) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}": tipo no permitido (use JPG, PNG, WEBP, HEIC o PDF)`);
        continue;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`"${file.name}": demasiado grande (máx. ${MAX_FILE_MB} MB)`);
        continue;
      }
      try {
        const res = await uploadComprobantePago(file);
        setComprobantes((prev) => [...prev, res.data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Error subiendo "${file.name}"`);
      }
    }
    setUploadingComprobante(false);
  };

  const handleRemoveComprobante = async (index) => {
    const item = comprobantes[index];
    if (!item) return;
    try {
      await deleteComprobantePago(item.url);
    } catch (err) {
      console.warn('No se pudo eliminar el comprobante del Storage:', err?.response?.data?.detail || err?.message || err);
    }
    setComprobantes((prev) => prev.filter((_, i) => i !== index));
  };

  const registrarPago = async () => {
    try {
      if (!formPago.factura_id || !formPago.monto || parseFloat(formPago.monto) <= 0) {
        toast.error('Seleccione una factura y monto mayor a 0');
        return;
      }
      // Validar que el monto no exceda el saldo (solo en creación; en
      // edición el backend recompone los pagos correctamente).
      if (!editingPagoId) {
        const fac = facturasPendientes.find((f) => (f.id || f._id) === formPago.factura_id);
        const saldo = Number(fac?.saldo || 0);
        const monto = parseFloat(formPago.monto);
        if (monto > saldo + 0.01) {
          toast.error(
            `El monto ($${monto.toFixed(2)}) excede el saldo pendiente ($${saldo.toFixed(2)}). Ajústalo o registra solo el abono.`,
          );
          return;
        }
      }
      setSavingPago(true);
      const comprobantesPayload = comprobantes.map((c) => ({
        url: c.url,
        filename: c.filename || '',
        content_type: c.content_type || '',
        size: c.size || 0,
      }));
      if (editingPagoId) {
        await updatePago(editingPagoId, {
          fecha: formPago.fecha,
          monto: parseFloat(formPago.monto),
          metodo_pago: formPago.metodo_pago,
          referencia: formPago.referencia,
          observaciones: formPago.observaciones,
          comprobantes: comprobantesPayload,
        });
        toast.success('Pago actualizado y saldo recalculado');
      } else {
        await api.post('/pagos', {
          ...formPago,
          monto: parseFloat(formPago.monto),
          comprobantes: comprobantesPayload,
        });
        toast.success('Pago registrado exitosamente');
      }
      setModalPagoOpen(false);
      setEditingPagoId(null);
      setComprobantes([]);
      cargarDatos();
    } catch (error) {
      console.error('Error guardando pago:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el pago');
    } finally {
      setSavingPago(false);
    }
  };

  const confirmarEliminarPago = async () => {
    if (!deletingPago) return;
    setDeletingPagoInProgress(true);
    try {
      // Si es un GRUPO (abono con múltiples facturas), usar el endpoint de grupo
      if (deletingPago.grupo_id) {
        await deleteAbonoGrupo(deletingPago.grupo_id);
        toast.success('Abono eliminado y facturas restauradas');
      } else if (deletingPago.items && deletingPago.items.length > 0) {
        // Fila del historial nueva pero sin grupo (single-row): borrar el pago único
        await deletePago(deletingPago.items[0].id || deletingPago.items[0]._id);
        toast.success('Pago eliminado y saldo recalculado');
      } else {
        await deletePago(deletingPago.id || deletingPago._id);
        toast.success('Pago eliminado y saldo recalculado');
      }
      setDeletingPago(null);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pago');
    } finally {
      setDeletingPagoInProgress(false);
    }
  };

  const exportarPagosExcel = () => {
    const ok = exportToExcel(
      pagos,
      [
        { label: 'Fecha', accessor: (p) => formatDate(p.fecha) },
        { label: 'N° Factura', key: 'factura_numero' },
        { label: 'Cliente', key: 'cliente_nombre' },
        { label: 'Monto', accessor: (p) => Number(p.monto || 0).toFixed(2) },
        { label: 'Método', key: 'metodo_pago' },
        { label: 'Referencia', key: 'referencia' },
        { label: 'Observaciones', key: 'observaciones' },
      ],
      'pagos_cxc',
      'Pagos',
    );
    if (!ok) toast.error('No hay pagos para exportar');
    else toast.success('Excel descargado');
  };

  const verEstadoCuenta = async (clienteId) => {
    try {
      const response = await api.get(`/cxc/estado-cuenta/${clienteId}`);
      setEstadoCuenta(response.data);
      setClienteSeleccionado(response.data.cliente);
      setModalEstadoCuentaOpen(true);
    } catch (error) {
      console.error('Error cargando estado de cuenta:', error);
      toast.error('Error al cargar estado de cuenta');
    }
  };

  const facturasFiltradas = filtroCliente && filtroCliente !== 'all'
    ? facturasPendientes.filter((f) => f.cliente_id === filtroCliente)
    : facturasPendientes;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cuentas-por-cobrar-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-700 text-foreground">
          Cuentas por Cobrar
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Control de crédito y cobranza</p>
      </div>

      <KPICards resumenCxC={resumenCxC} facturasPendientes={facturasPendientes} />

      <Tabs defaultValue="abonos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="abonos" data-testid="tab-estado-cliente">Estado por Cliente</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="tab-facturas">Facturas Pendientes</TabsTrigger>
          <TabsTrigger value="pagos" data-testid="tab-pagos">Historial de Pagos</TabsTrigger>
        </TabsList>

        <TabsContent value="abonos" className="space-y-4">
          <AbonoClienteTab
            clientes={clientes}
            onDataChanged={cargarDatos}
            comprobantes={comprobantes}
            onSelectComprobantes={handleSelectComprobantes}
            onRemoveComprobante={handleRemoveComprobante}
            uploadingComprobante={uploadingComprobante}
            onClearComprobantes={() => setComprobantes([])}
          />
        </TabsContent>

        <TabsContent value="facturas" className="space-y-4">
          <FacturasPendientesTab
            facturasFiltradas={facturasFiltradas}
            clientes={clientes}
            filtroCliente={filtroCliente}
            setFiltroCliente={setFiltroCliente}
            onPagar={abrirModalPago}
          />
        </TabsContent>

        <TabsContent value="pagos" className="space-y-4">
          <HistorialPagosTab
            pagos={pagos}
            onExportarExcel={exportarPagosExcel}
            onVerComprobantes={setVerComprobantesPago}
            onEditPago={abrirModalEditarPago}
            onDeletePago={setDeletingPago}
          />
        </TabsContent>
      </Tabs>

      {/* Modal NUEVO: registrar pago por cliente (FIFO). Reutilizado en modo edición para editar un abono existente. */}
      <PagoDialog
        open={modalRegistrarOpen}
        onOpenChange={(o) => {
          setModalRegistrarOpen(o);
          if (!o) { setRegistrarClienteId(null); setComprobantes([]); setEditingAbono(null); }
        }}
        clientes={clientes}
        initialClienteId={registrarClienteId}
        comprobantes={comprobantes}
        onSelectComprobantes={handleSelectComprobantes}
        onRemoveComprobante={handleRemoveComprobante}
        onSetComprobantes={setComprobantes}
        uploadingComprobante={uploadingComprobante}
        editingAbono={editingAbono}
        onSaved={() => { setComprobantes([]); setEditingAbono(null); cargarDatos(); }}
      />

      {/* Modal de EDICIÓN de un pago existente (single-row). */}
      <PagoEditDialog
        open={modalPagoOpen}
        onOpenChange={(o) => { setModalPagoOpen(o); if (!o) setEditingPagoId(null); }}
        formPago={formPago}
        setFormPago={setFormPago}
        facturaAsociada={facturasPendientes.find((f) => (f.id || f._id) === formPago.factura_id)}
        comprobantes={comprobantes}
        onSelectComprobantes={handleSelectComprobantes}
        onRemoveComprobante={handleRemoveComprobante}
        uploadingComprobante={uploadingComprobante}
        saving={savingPago}
        onSave={registrarPago}
      />

      <EstadoCuentaDialog
        open={modalEstadoCuentaOpen}
        onOpenChange={setModalEstadoCuentaOpen}
        cliente={clienteSeleccionado}
        estadoCuenta={estadoCuenta}
        onVerComprobantes={setVerComprobantesPago}
      />

      <VerComprobantesDialog
        pago={verComprobantesPago}
        onClose={() => setVerComprobantesPago(null)}
      />

      <AlertDialog open={!!deletingPago} onOpenChange={(o) => { if (!o) setDeletingPago(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-pago">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este pago?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el pago de <strong>{formatCurrency(deletingPago?.monto || 0)}</strong> de la factura{' '}
              <strong>{deletingPago?.factura_numero}</strong> y el saldo se recalculará automáticamente.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingPagoInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarEliminarPago}
              disabled={deletingPagoInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-eliminar-pago"
            >
              {deletingPagoInProgress ? 'Eliminando...' : 'Eliminar Pago'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
